import clsx from 'clsx';
import { Keyboard } from 'swiper/modules';
import type { Swiper } from 'swiper/types';
import { useSwiper } from 'swiper/react';
import { Button } from '@marriott/mi-ui-library';
import { CarouselSwiper, CarouselSlideWrapper } from './Carousel.styles';
import { CarouselBaseProps, CarouselButtonProps, CarouselSlideProps } from './Carousel.types';
import { getClickTrackValue } from '../../utils/getClickTrackValue';
import 'swiper/css';
import 'swiper/css/pagination';
import { useCarouselContext } from './Carousel.context';
import { useViewportCategory } from '../../utils/hooks/useViewportCategory';

export function CarouselSlide({ children, className, onClick, shouldSetIsInitialized }: CarouselSlideProps) {
  const { isInitialized } = useCarouselContext();

  return (
    <CarouselSlideWrapper
      className={clsx(className, shouldSetIsInitialized && !isInitialized && 'swiper-slide-active')} // always apply active class to first slide for server render
      onClick={onClick}
    >
      {children}
    </CarouselSlideWrapper>
  );
}

export const CarouselButton = ({
  className,
  direction,
  onClick,
  trackingProperties,
  trackingPropertyFallbacks,
}: CarouselButtonProps) => {
  const swiper = useSwiper();
  const isPrevious = direction === 'left';
  const arrowClassName = isPrevious ? 'carousel-left-arrow' : 'carousel-right-arrow';
  const customClickTrackValue = getClickTrackValue({
    trackingProperties,
    fallbacks: trackingPropertyFallbacks,
  });

  return (
    <Button
      callback={() => {
        onClick?.();
        isPrevious ? swiper.slidePrev() : swiper.slideNext();
      }}
      className={clsx(className, arrowClassName)}
      custom_click_track_value={customClickTrackValue}
      testId={arrowClassName}
      ariaLabel={arrowClassName}
      trackingProperties={trackingProperties}
    >
      <span className={isPrevious ? 'icon-arrow-left' : 'icon-arrow-right'} />
    </Button>
  );
};

export function CarouselBase({ children, className, options, onTouchEnd, onSwiper }: CarouselBaseProps) {
  const { setSwiperTouched, setIsInitialized, swiperRef, setProgress } = useCarouselContext();
  const { modules = [] } = options || {};
  const viewportCategory = useViewportCategory(); // This is added to rerender the carousel when width changes - there is a bug that can show when pagination dots need to be added and are not previously there.

  return (
    <CarouselSwiper
      onTouchEnd={(...touchEndArguments: [swiper: Swiper, event: MouseEvent | TouchEvent | PointerEvent]) => {
        setSwiperTouched(() => true);
        onTouchEnd?.(...touchEndArguments);
      }}
      onSwiper={(swiper: Swiper) => {
        swiperRef.current = swiper;
        setIsInitialized(() => true);
        onSwiper?.();
      }}
      onProgress={(_: Swiper, progress: number) => {
        setProgress(() => progress);
      }}
      slidesPerView="auto"
      centeredSlides
      modules={[Keyboard, ...modules]}
      keyboard={{ enabled: true }}
      className={className}
      {...options}
      key={viewportCategory}
    >
      {children}
    </CarouselSwiper>
  );
}
